import React, { useState } from 'react';
import { ArrowRight, MapPin, Phone, Mail, Clock, Send, Facebook, Instagram, Linkedin, CheckCircle, AlertCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState(''); // '', 'sending', 'success', 'error'
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setFormStatus('sending');

  try {
    const response = await fetch('https://gusgroupltd.com/api/send-email.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        type: 'contact',
        website: '' // Honeypot field - leave empty
      }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        service: '',
        message: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus('');
      }, 5000);
    } else {
      setFormStatus('error');
      setTimeout(() => {
        setFormStatus('');
      }, 5000);
    }
  } catch (error) {
    console.error('Error:', error);
    setFormStatus('error');
    setTimeout(() => {
      setFormStatus('');
    }, 5000);
  }
};

  const services = [
    "Remodeling & Renovations",
    "Road Construction & Civil Works",
    "Real Estate Services",
    "Furniture Supply & Design",
    "Interior & Exterior Decoration",
    "Building Development & Construction",
    "Importation & Exportation",
    "Sales & Purchase of Properties",
    "Consultancy Services",
    "Facility Management & Maintenance"
  ];

  const contactInfo = [
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Visit Our Office",
      details: [
        "Opposite HGL Guest House",
        "Nii Afutu Tsuru St",
        "Accra, Ghana"
      ],
      link: "https://maps.google.com/?q=Nii+Afutu+Tsuru+St+Accra",
      linkText: "Get Directions"
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Call Us",
      details: [
        "Main: 0592668258",
        "Office: +233 30 123 4567",
        "Emergency: +233 50 987 6543"
      ],
      link: "tel:0592668258",
      linkText: "Call Now"
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Email Us",
      details: [
        "General: info@gusgroupltd.com",
        "Projects: projects@gusgroupltd.com",
        "Support: support@gusgroupltd.com"
      ],
      link: "mailto:info@gusgroupltd.com",
      linkText: "Send Email"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Working Hours",
      details: [
        "Monday - Friday: 8:00 AM - 6:00 PM",
        "Saturday: 9:00 AM - 3:00 PM",
        "Sunday: Closed"
      ],
      link: null,
      linkText: null
    }
  ];

  const offices = [
    {
      city: "Accra (Head Office)",
      address: "Opposite HGL Guest House, Nii Afutu Tsuru St",
      phone: "0592668258",
      email: "accra@gusgroupltd.com"
    },
    {
      city: "Kumasi",
      address: "Adum, Harper Road, Kumasi",
      phone: "+233 50 234 5678",
      email: "kumasi@gusgroupltd.com"
    },
    {
      city: "Takoradi",
      address: "Market Circle, Takoradi",
      phone: "+233 50 345 6789",
      email: "takoradi@gusgroupltd.com"
    }
  ];

  const faqs = [
    {
      question: "What areas do you serve?",
      answer: "We serve all regions of Ghana with offices in Accra, Kumasi, and Takoradi. We also undertake projects in neighboring West African countries."
    },
    {
      question: "How long does a typical project take?",
      answer: "Project timelines vary based on scope and complexity. Residential projects typically take 6-18 months, while commercial projects can take 12-36 months. We provide detailed timelines during consultation."
    },
    {
      question: "Do you provide free consultations?",
      answer: "Yes! We offer free initial consultations to understand your needs and provide preliminary advice. Contact us to schedule your consultation."
    },
    {
      question: "What is your payment structure?",
      answer: "We typically work with milestone-based payments: 40% at contract signing, 30% at design approval, 20% at development completion, and 10% at project handover."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes, GUS Group Ltd is fully licensed by the Ghana Standards Authority and holds comprehensive insurance coverage for all our projects."
    },
    {
      question: "Can I visit your completed projects?",
      answer: "Absolutely! We can arrange site visits to our completed projects (with client permission) so you can see our work quality firsthand."
    }
  ];

  return (
    <div className="bg-black text-white">
      <Navigation onQuoteClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&h=1080&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
            Get In Touch
          </h2>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Let's Build
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              Something Great
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Ready to start your project? We're here to help bring your vision to life
          </p>
        </div>
      </section>

      {/* Quick Contact Info */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all"
              >
                <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center mb-6">
                  <div className="text-yellow-400">
                    {info.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  {info.title}
                </h3>
                <div className="space-y-2 mb-6">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-400">
                      {detail}
                    </p>
                  ))}
                </div>
                {info.link && (
                  
                <a    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-yellow-400 font-semibold flex items-center gap-2 hover:gap-4 transition-all"
                  >
                    {info.linkText}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold mb-4">Send Us a Message</h2>
              <p className="text-gray-400 mb-8">
                Fill out the form below and we'll get back to you within 24 hours
              </p>

              {formStatus === 'success' && (
                <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-green-500 font-bold mb-1">Message Sent Successfully!</h4>
                    <p className="text-gray-300 text-sm">Thank you for contacting us. We'll respond within 24 hours.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all"
                      placeholder="0501234567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all"
                      placeholder="Project Inquiry"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Service Interested In
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all"
                  >
                    <option value="">Select a service (optional)</option>
                    {services.map((service, i) => (
                      <option key={i} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Your Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all resize-none"
                    placeholder="Tell us about your project, questions, or how we can help..."
                  ></textarea>
                </div>
                {/* Form Status Messages */}
                {formStatus === 'sending' && (
                  <div className="mb-6 bg-blue-500/10 border border-blue-500/50 rounded-xl p-4 flex items-start gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <div>
                      <h4 className="text-blue-500 font-bold mb-1">Sending Message...</h4>
                      <p className="text-gray-300 text-sm">Please wait while we process your request.</p>
                    </div>
                  </div>
                )}

                {formStatus === 'success' && (
                  <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-green-500 font-bold mb-1">Message Sent Successfully!</h4>
                      <p className="text-gray-300 text-sm">Thank you for contacting us. We'll respond within 24 hours.</p>
                    </div>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-red-500 font-bold mb-1">Failed to Send Message</h4>
                      <p className="text-gray-300 text-sm">Please try again or contact us directly at info@gusgroupltd.com</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-yellow-400 text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  Send Message
                  <Send className="w-5 h-5" />
                </button>

                <p className="text-gray-400 text-sm text-center">
                  We respect your privacy. Your information will never be shared.
                </p>
              </form>
            </div>

            {/* Map & Additional Info */}
            <div className="space-y-8">
              {/* Google Map */}
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-yellow-400/20 overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    title="GUS Group Office Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3970.7176607415654!2d-0.18664668461!3d5.614818995976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNcKwMzYnNTMuMyJOIDDCsDExJzExLjkiVw!5e0!3m2!1sen!2sgh!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="grayscale hover:grayscale-0 transition-all duration-500"
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Head Office Location</h3>
                  <p className="text-gray-400 mb-4">
                    Opposite HGL Guest House, Nii Afutu Tsuru St, Accra
                  </p>
                  
                  <a  href="https://maps.google.com/?q=Nii+Afutu+Tsuru+St+Accra"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 font-semibold flex items-center gap-2 hover:gap-4 transition-all"
                  >
                    Get Directions
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20">
                <h3 className="text-2xl font-bold text-white mb-4">Connect With Us</h3>
                <p className="text-gray-400 mb-6">
                  Follow us on social media for project updates, industry insights, and more
                </p>
                <div className="flex gap-4">
                  
                <a    href="https://facebook.com/gusgroupltd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 bg-black rounded-full flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all transform hover:scale-110"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                  
                  <a  href="https://instagram.com/gusgroupltd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 bg-black rounded-full flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all transform hover:scale-110"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  
                  <a  href="https://linkedin.com/company/gusgroupltd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 bg-black rounded-full flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all transform hover:scale-110"
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                  
                  <a  href="https://wa.me/233592668258"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-all transform hover:scale-110"
                  >
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Offices */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Locations
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Visit Our Offices
            </h3>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We have offices across Ghana to serve you better
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offices.map((office, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all"
              >
                <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center mb-6">
                  <MapPin className="w-6 h-6 text-yellow-400" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">
                  {office.city}
                </h4>
                <div className="space-y-3 text-gray-400">
                  <p>{office.address}</p>
                  <a href={`tel:${office.phone}`} className="block hover:text-yellow-400 transition-colors">
                    {office.phone}
                  </a>
                  <a href={`mailto:${office.email}`} className="block hover:text-yellow-400 transition-colors">
                    {office.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              FAQs
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Frequently Asked Questions
            </h3>
            <p className="text-xl text-gray-400">
              Quick answers to common questions
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all"
              >
                <h4 className="text-xl font-bold text-white mb-3 flex items-start gap-3">
                  <span className="text-yellow-400 flex-shrink-0">Q:</span>
                  {faq.question}
                </h4>
                <p className="text-gray-400 pl-8">
                  <span className="text-yellow-400 font-bold">A:</span> {faq.answer}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">Still have questions?</p>
            
            <a  href="tel:0592668258"
              className="inline-flex items-center gap-2 bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105"
            >
              Call Us Now
              <Phone className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.1) 35px, rgba(0,0,0,.1) 70px)'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
            Contact us today and let's discuss how we can bring your vision to life
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-black text-yellow-400 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-900 transition-all transform hover:scale-105 flex items-center gap-2 justify-center"
            >
              Send Message
              <ArrowRight className="w-5 h-5" />
            </button>
            <a 
              href="tel:0592668258"
              className="border-2 border-black text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-black hover:text-yellow-400 transition-all"
            >
              Call: 0592668258
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* WhatsApp Floating Button */}
      
      <a  href="https://wa.me/233592668258?text=Hello%20GUS%20Group,%20I'm%20interested%20in%20your%20services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all transform hover:scale-110 z-40"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default ContactPage;